/**
 * 비로그인 목록과 계정 목록을 합치는 판정.
 *
 * **자동 병합은 어느 방향이든 사용자 데이터를 소리 없이 지운다** — 로컬 우선이면
 * 예전에 고른 것이, 서버 우선이면 방금 고른 것이 사라진다. 그래서 겹치면서
 * 내용이 다른 것만 골라 사용자에게 묻는다.
 *
 * 순수 함수로 둔 이유: 이 판정이 틀리면 사용자가 데이터를 잃는데, 화면 코드에
 * 섞여 있으면 검증할 방법이 없다. 여기 있으면 단위 테스트로 고정된다.
 */

import type { PickResponse } from '@lifetimeline/shared';

import type { LocalPick } from '../../lib/localPicks.js';

export interface MergeConflict {
  categoryId: string;
  /** 이 브라우저에서 고른 것 */
  local: LocalPick;
  /** 계정에 저장된 것 */
  server: PickResponse;
}

export interface MergePlan {
  /** 서버에 없는 것. 물어볼 필요 없이 추가한다 */
  toAdd: LocalPick[];
  /** 양쪽이 같은 것. 아무것도 안 해도 된다 */
  identical: LocalPick[];
  /** 양쪽에 있는데 내용이 다른 것. **사용자가 골라야 한다** */
  conflicts: MergeConflict[];
}

export type ConflictChoice = 'local' | 'server';

/**
 * 두 목록이 "같은 Pick"인지 본다. **이름과 설명만 본다.**
 *
 * 사진은 비교할 수 없다 — 로컬 사진은 아직 브라우저 안의 Blob이라 주소가 없고,
 * 주소를 만들려면 올려야 하는데 여기서 올리면 "서버 것을 유지"로 끝날 Pick의
 * 사진까지 올라가 고아 파일이 된다. 그래서 판정을 먼저 하고 업로드는 실제로
 * 저장할 것만 한다.
 *
 * 이름과 설명이 **글자 하나까지 같으면** 같은 선택을 다시 적은 것으로 본다.
 * 이때 로컬 사진은 올리지 않으므로, 같은 항목을 다시 담으며 사진만 바꾼 경우
 * 그 사진은 반영되지 않는다. 계정 쪽 사진을 지우는 것보다는 낫다고 봤다 —
 * 지우면 되돌릴 수 없지만, 안 올린 사진은 로그인한 뒤 다시 넣으면 된다.
 *
 * 비교 전에 다듬는다: 서버는 빈 문자열을 필드 없음으로 저장하므로,
 * `''`와 `undefined`를 다르다고 보면 실제로는 같은 Pick이 충돌로 잡힌다.
 */
function isSamePick(local: LocalPick, server: PickResponse): boolean {
  const normalize = (value: string | undefined): string => (value ?? '').trim();

  return (
    normalize(local.itemName) === normalize(server.itemName) &&
    normalize(local.itemDescription) === normalize(server.itemDescription)
  );
}

/**
 * 병합 계획을 세운다.
 *
 * @param localPicks 이 브라우저에 있던 것
 * @param serverPicks 계정에 저장돼 있던 것
 */
export function buildMergePlan(
  localPicks: readonly LocalPick[],
  serverPicks: readonly PickResponse[],
): MergePlan {
  const serverByCategory = new Map(serverPicks.map((pick) => [pick.categoryId, pick]));

  const toAdd: LocalPick[] = [];
  const identical: LocalPick[] = [];
  const conflicts: MergeConflict[] = [];

  for (const local of localPicks) {
    const server = serverByCategory.get(local.categoryId);

    if (server === undefined) {
      toAdd.push(local);
    } else if (isSamePick(local, server)) {
      identical.push(local);
    } else {
      conflicts.push({ categoryId: local.categoryId, local, server });
    }
  }

  // 서버에만 있는 것은 계획에 넣지 않는다 — 건드리지 않고 그대로 둔다.
  // 배치 업서트는 보낸 것만 덮어쓰므로 목록에서 빼면 그대로 남는다.

  return { toAdd, identical, conflicts };
}

/**
 * 사용자의 선택을 실제로 보낼 Pick 목록으로 바꾼다.
 *
 * `server`를 고른 충돌은 **보내지 않는다** — 업서트는 보낸 것만 덮어쓰므로
 * 빼면 서버 값이 그대로 남는다. 굳이 서버 값을 되돌려 보내면 `updatedAt`만
 * 바뀌어 "고치지도 않았는데 수정된" 상태가 된다.
 */
export function resolveMergePlan(
  plan: MergePlan,
  choices: ReadonlyMap<string, ConflictChoice>,
): LocalPick[] {
  const result: LocalPick[] = [...plan.toAdd];

  for (const conflict of plan.conflicts) {
    // 고르지 않았으면 서버를 남긴다 — 기본값은 "이미 저장된 것을 지키는" 쪽이다.
    // 사용자가 명시적으로 고른 경우에만 덮어쓴다.
    if (choices.get(conflict.categoryId) === 'local') {
      result.push(conflict.local);
    }
  }

  return result;
}

/** 물어볼 게 있는가. 없으면 화면을 띄우지 않고 바로 적용한다. */
export function needsUserDecision(plan: MergePlan): boolean {
  return plan.conflicts.length > 0;
}

/** 보낼 게 하나도 없는가. 없으면 API도 부르지 않는다. */
export function isNoop(plan: MergePlan): boolean {
  return plan.toAdd.length === 0 && plan.conflicts.length === 0;
}
